import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    Grid,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemSecondaryAction,
    ListItemText,
    Link as MuiLink,
    Skeleton,
    Stack,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AdvancedMarker,
    APIProvider,
    Map as GoogleMap,
} from '@vis.gl/react-google-maps';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
    useGetAllAssetsQuery,
    useGetCompanyGuestsInfoQuery,
    useGetOwnerProfileQuery,
    useGetReviewsByModuleQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import { camelCaseToTitleCase, capitalizeName } from '../../../shared/utils';
import Modal from '../../shared/Modal';
import ExperienceReviewsSection from '../Reviews/ExperienceReviewsSection';
import { ReviewsCustomTheme } from '../Reviews/ReviewsCustomBreakpoints';
import AmenitiesComponent from './Amenities/AmenitiesComponent';
import CalendarComponent from './Calendar/CalendarComponent';
import ContactForm from './ContactForm';
import IndividualExperienceCharges from './IndividualExperienceCharges';
import IndividualExperienceSpaces from './IndividualExperienceSpaces';
import { Circle } from './Maps/Circle';
import LiveLocationCircle from './Maps/LiveLocationCircle';
import { EXPERIENCE_TYPE_ICON } from './Maps/MapMarker';
import ExperiencePolicyCard from './Policies/ExperiencePolicyCard';
import SimilarExperiences from './SimilarExperiences/SimilarExperiences';
import { AvailabilityMode } from '../../enums/experience';
import { useParamState } from '../../hooks/useParamState';
import { FormValues as Experience } from '../../types/experience';
import { ArrowBack, NavigateNext } from '@mui/icons-material';
import { formatHireRange } from './IndividualExperienceDetails';
import IndividualExperienceHero from './IndividualExperienceHero';

const HostProfileHeader = ({ value }: { value: any }) => {
    const { data: ownerProfile, isLoading } = useGetOwnerProfileQuery(
        value?.owner?.id,
        {
            skip: !value?.owner?.id,
        },
    );

    const getTitleText = (v: any) => {
        if (!v?.owner?.name || !v?.details?.subType) return '';
        switch (v?.type) {
            case 'property':
                return `Entire ${v.details.subType} hosted by ${v?.owner.name}`;
            case 'boat':
                return `Entire boat hosted by ${v?.owner.name}`;
            case 'commercial':
                if (v?.details?.subType === 'office')
                    return `Office space provided by ${v?.owner.name}`;
                if (v?.details?.subType === 'house')
                    return `Home office provided by ${v?.owner.name}`;
                break;
            default:
                return `${capitalizeName(v?.details?.subType)} supplied by ${
                    v?.owner.name
                }`;
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1,
                }}
            >
                <Skeleton variant='circular' height={50} width={50} />
                <div>
                    <Skeleton variant='text' height={30} width={300} />
                    <Skeleton variant='text' height={20} width={150} />
                </div>
            </Box>
        );
    }
    if (!ownerProfile) {
        return (
            <Typography
                variant='body1'
                component='h2'
                sx={{ fontWeight: 'bold', py: 1 }}
            >
                {getTitleText(value)}
            </Typography>
        );
    }

    return (
        <MuiLink
            component={Link}
            to={`/hosts/${ownerProfile.id}`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1,
            }}
        >
            <Avatar
                src={ownerProfile.logo?.src}
                sx={(theme) => ({
                    bgcolor: 'divider',
                    height: 50,
                    width: 50,
                    outline: '1px solid',
                    outlineColor: theme.palette.green.main,
                    outlineOffset: '2px',
                })}
            />
            <div>
                <Typography
                    variant='body1'
                    component='h2'
                    sx={{ fontWeight: 'bold' }}
                >
                    {getTitleText(value)}
                    <NavigateNext color='primary' sx={{ ml: 1 }} />
                </Typography>
                <Typography variant='body2'>
                    Hosting since{' '}
                    {new Date(ownerProfile.createdAt).getFullYear()}
                </Typography>
            </div>
        </MuiLink>
    );
};

export default function IndividualExperienceContent(experience: any) {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const loggedInUser = UserRole();
    const [openModal, setOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalComponent, setModalComponent] = useState('');

    const { data: allExperiences } = useGetAllAssetsQuery({ url: '/modules' });
    const { data: onCreditHold } = useGetCompanyGuestsInfoQuery(
        `/guests/${loggedInUser?.company}`,
        { skip: !loggedInUser?.role.includes('guest') },
    );

    const value = experience?.experience;
    const { data: reviewsData } = useGetReviewsByModuleQuery(value?.id);
    const [month] = useParamState('month', {
        type: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
        defaultValue: format(new Date(), 'yyyy-MM'),
    });

    const days = [
        { day: 'monday', value: value?.monday },
        { day: 'tuesday', value: value?.tuesday },
        { day: 'wednesday', value: value?.wednesday },
        { day: 'thursday', value: value?.thursday },
        { day: 'friday', value: value?.friday },
        { day: 'saturday', value: value?.saturday },
        { day: 'sunday', value: value?.sunday },
    ];

    const capacity: any = {
        dedicatedDesks: value?.details?.desks,
        privateOffices: value?.details?.privateOffices,
        meetingRooms: value?.details?.meetingRooms,
    };

    const availableDays = days.filter((i: any) => i.value === true);

    const onClick = () => {
        navigate(`/reservations/ai-assistant`, {
            state: { page: 'experience', id: value?.id, name: value?.name },
        });
    };

    const fullAddressPickup = `${value?.streetAddress} ${value?.suburb} ${value?.postcode} ${value?.state} ${value?.countryCode}`;
    const fullAddressDropoff = `${value?.details?.dropoffStreetAddress} ${value?.details?.dropoffSuburb} ${value?.details?.dropoffPostcode} ${value?.details?.dropoffState}`;

    const rules: string[] = [];
    if (value?.details?.minimumLicenceRequired) {
        rules.push(
            `Minimum licence required: ${capitalizeName(
                value.details.minimumLicenceRequired,
            )}`,
        );
    }
    if (value?.details?.minimumDriversAge) {
        rules.push(`Minimum driver's age: ${value.details.minimumDriversAge}`);
    }
    if (value?.details?.petsAllowed === false) {
        rules.push('Pets not allowed');
    }
    if (value?.details?.smokingAllowed === false) {
        rules.push('Smoking, vaping, e-cigarettes not allowed');
    }
    if (value?.details?.rules) {
        rules.push(value.details.rules);
    }

    const shouldShowButton = (
        u: { role: string | string[]; viewOnly: any; company: any },
        v: {
            isAvailable: any;
            availabilityMode: AvailabilityMode;
            status: string;
        },
        exp: { experience: { owner: { id: any } } },
    ) => {
        if (onCreditHold?.onCreditHold) return false;
        if (v.availabilityMode === AvailabilityMode.AddOn) return false;
        if (v.status === 'draft') return false;

        return u?.role.includes('owner')
            ? !u?.viewOnly &&
                  v?.isAvailable &&
                  exp?.experience?.owner.id === u?.company
            : u?.role.includes('guest')
              ? !u?.viewOnly && v?.isAvailable
              : false;
    };

    const isPropertyOrBoat =
        value?.type === 'property' || value?.type === 'boat';
    const isVehicle = value?.type === 'vehicle';
    const isCommercialOfficeOrHouse =
        value?.type === 'commercial' &&
        ['office', 'house'].includes(value.details.subType);

    const renderPropertyOrBoatDetails = () => (
        <Stack direction='row' flexWrap='wrap' gap={0.8}>
            <Typography component={'h3'} variant='visuallyHidden'>
                Specifications
            </Typography>
            {!['commercial', 'office space'].includes(
                value?.details?.subType,
            ) && (
                <>
                    <Chip
                        size='small'
                        label={`Sleeps ${value?.details?.overnightGuests ?? '-'}`}
                    />
                    <Chip
                        size='small'
                        label={`Bedrooms ${value?.details?.bedrooms ?? '-'}`}
                    />
                    <Chip
                        size='small'
                        label={`Beds ${value?.details?.beds ?? '-'}`}
                    />
                </>
            )}
            <Chip
                size='small'
                label={`Bathrooms ${value?.details?.baths ?? '-'}`}
            />
            <Chip
                size='small'
                color='primary'
                variant='outlined'
                label={`Max guests ${value?.details?.maxGuests ?? '-'}`}
            />
        </Stack>
    );

    const renderVehicleDetails = () => (
        <Stack direction='row' flexWrap='wrap' gap={0.8}>
            <Typography component={'h3'} variant='visuallyHidden'>
                Specifications
            </Typography>
            <Chip
                size='small'
                label={`${value?.details?.make || ''} ${value?.details?.model || ''} ${
                    value?.details?.subType === 'car'
                        ? capitalizeName(value?.details?.bodyType)
                        : ''
                } ${value?.details?.year || ''}`.trim()}
            />
            <Chip
                size='small'
                label={`Transmission ${capitalizeName(
                    value?.details?.transmission === 'cvt'
                        ? 'CVT'
                        : value?.details?.transmission,
                )}`}
            />
            <Chip
                size='small'
                label={`Fuel ${capitalizeName(value?.details?.fuelType)}`}
            />
            {value?.details?.subType === 'motorbike' && (
                <Chip
                    size='small'
                    label={`Engine CC ${value?.details?.engineCC ?? '-'}`}
                />
            )}
            {value?.details?.subType === 'car' && (
                <>
                    <Chip
                        size='small'
                        label={`Seats ${value?.details?.numberOfSeats ?? '-'}`}
                    />
                    <Chip
                        size='small'
                        label={`Doors ${value?.details?.numberOfDoors ?? '-'}`}
                    />
                </>
            )}
        </Stack>
    );

    const renderCommercialDetails = () => (
        <Stack direction='row' flexWrap='wrap' gap={0.8}>
            <Typography component={'h3'} variant='visuallyHidden'>
                Specifications
            </Typography>
            {Object.keys(capacity)
                .filter((k) => capacity[k] > 0)
                .map((k) => {
                    const label =
                        k === 'privateOffices' &&
                        value?.details?.subType === 'house'
                            ? 'Rooms'
                            : camelCaseToTitleCase(k);
                    return (
                        <Chip
                            key={k}
                            size='small'
                            label={`${label} ${capacity[k]}`}
                        />
                    );
                })}
            {Object.keys(capacity).filter((k) => capacity[k] > 0).length ===
                0 && <Chip size='small' label='Please select the dates' />}
        </Stack>
    );

    const [showAll, setShowAll] = React.useState(false);

    const handleCloseModal = () => setOpen(false);

    const position = value?.lastSeenAt?.lat
        ? value.lastSeenAt
        : { lat: value?.lat, lng: value?.lng };

    const policy = value?.policy || {
        cancellationType: 'fair',
        description: '',
        name: 'Cancellation Policy',
        policyId: 0,
        requiresConsent: false,
    };

    return (
        <Box
            sx={{
                pt: 3,
                [theme.breakpoints.down('md')]: {
                    px: 2,
                },
            }}
        >
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems='flex-start'>
                <Grid item xs={12} md={7}>
                    <Stack spacing={{ xs: 2.5, sm: 3 }}>
                        <Box>
                            <Stack spacing={1.5}>
                                {isMobile && (
                                    <>
                                        <Typography variant='h1' sx={{ pt: 2 }}>
                                            {value?.name}
                                        </Typography>
                                        <IndividualExperienceHero
                                            experience={value}
                                        />
                                    </>
                                )}
                                <HostProfileHeader value={value} />
                                <Grid
                                    sx={{
                                        width: '100%',
                                        height: isMobile ? '2%' : '3%',
                                        justifyContent: 'left',
                                        display: { xs: 'flex', md: 'none' },
                                        flexDirection: {
                                            xs: 'column',
                                            md: 'column',
                                        },
                                    }}
                                >
                                    {isMobile && (
                                        <>
                                            {isPropertyOrBoat ? (
                                                renderPropertyOrBoatDetails()
                                            ) : isVehicle ? (
                                                renderVehicleDetails()
                                            ) : isCommercialOfficeOrHouse ? (
                                                renderCommercialDetails()
                                            ) : (
                                                <Typography
                                                    variant='subHeading'
                                                    sx={{
                                                        display: {
                                                            xs: 'none',
                                                            md: 'flex',
                                                        },
                                                    }}
                                                >
                                                    {value?.details.quantity} x{' '}
                                                    {value?.name}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </Grid>
                                {!isMobile && (
                                    <>
                                        {isPropertyOrBoat ? (
                                            renderPropertyOrBoatDetails()
                                        ) : isVehicle ? (
                                            renderVehicleDetails()
                                        ) : isCommercialOfficeOrHouse ? (
                                            renderCommercialDetails()
                                        ) : (
                                            <Typography
                                                variant='subHeading'
                                                sx={{
                                                    display: {
                                                        xs: 'none',
                                                        md: 'flex',
                                                    },
                                                }}
                                            >
                                                {value?.details.quantity} x{' '}
                                                {value?.name}
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Box>

                        <Box>
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                <Typography variant='h2'>
                                    {value?.type === 'property'
                                        ? 'Property'
                                        : value?.type === 'boat'
                                          ? 'Boat'
                                          : value?.type === 'vehicle'
                                            ? 'Vehicle'
                                            : value?.type === 'commercial'
                                              ? capitalizeName(
                                                    value?.details?.subType,
                                                )
                                              : 'Activity'}{' '}
                                    Description
                                </Typography>
                                <Typography variant='body1'>
                                    {value?.description}
                                </Typography>

                                {!!(
                                    value?.linkedModules?.length > 0 ||
                                    value?.mainModule
                                ) && (
                                    <Typography variant='body1' sx={{ pt: 3 }}>
                                        <span>
                                            Note: This experience is linked to
                                            another experience owned by the
                                            host. When making a booking for this
                                            experience, it will{' '}
                                            <strong>
                                                {value?.combinedModules
                                                    ?.length === 0
                                                    ? 'block'
                                                    : 'combine'}
                                            </strong>{' '}
                                            {value?.combinedModules?.length ===
                                            0
                                                ? 'the other experience. However, it does not combine the facilities and you will not be charged additional for this – '
                                                : 'the experiences and provide access to both facilities at no additional cost. The number of bedrooms/guests listed above is the total across both experiences – '}
                                            <Link
                                                to={`/viewExperiences/${
                                                    value.linkedModules?.[0]
                                                        ?.type ||
                                                    value?.mainModule?.type
                                                }/${
                                                    value.linkedModules?.[0]
                                                        ?.id ||
                                                    value?.mainModule?.id
                                                }`}
                                                style={{
                                                    color: theme.palette
                                                        .textAccent.main,
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                {value.linkedModules?.[0]
                                                    ?.name ||
                                                    value?.mainModule?.name}
                                            </Link>
                                        </span>
                                    </Typography>
                                )}

                                <Typography variant='h2' sx={{ pt: 1.5 }}>
                                    Reservation Information
                                </Typography>
                                <Typography variant='body1'>
                                    <ul style={{ paddingLeft: 20, margin: 0 }}>
                                        <li>
                                            This experience accepts{' '}
                                            {value?.visitType} reservations only
                                            for{' '}
                                            {availableDays.map((name, i) => (
                                                <span key={i}>
                                                    {i > 0 && ','}{' '}
                                                    {name.day
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        name.day.slice(1)}
                                                </span>
                                            ))}
                                        </li>
                                        <li>
                                            This experience{' '}
                                            {value?.publicHolidays
                                                ? 'accepts'
                                                : "doesn't accept"}{' '}
                                            reservations for public holidays.
                                        </li>
                                        <li>
                                            {value?.type === 'commercial'
                                                ? `Access from ${value?.checkinTime} to ${value?.checkoutTime}`
                                                : `Checkin from ${value?.checkinTime} and checkout before ${value?.checkoutTime}`}
                                        </li>
                                        <li>
                                            The min/max hire period is{' '}
                                            {formatHireRange(value)}
                                        </li>
                                    </ul>
                                </Typography>

                                {value?.type === 'boat' && (
                                    <Box sx={{ pt: 3 }}>
                                        <Typography
                                            variant='h3'
                                            sx={{
                                                mb: 1.5,
                                            }}
                                        >
                                            What this boat offers
                                        </Typography>
                                        <Box
                                            sx={{
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                width: '100%',
                                                pt: 2,
                                            }}
                                        >
                                            <AmenitiesComponent
                                                amenities={value?.details}
                                            />
                                        </Box>
                                    </Box>
                                )}

                                {value?.type === 'vehicle' &&
                                    rules?.length > 0 && (
                                        <>
                                            <Typography
                                                variant='h3'
                                                sx={{ pt: 2 }}
                                            >
                                                Hiring Rules
                                            </Typography>
                                            <Typography
                                                variant='body1'
                                                sx={{
                                                    listStyle:
                                                        rules?.length === 1
                                                            ? 'none'
                                                            : 'default',
                                                }}
                                            >
                                                {rules.map(
                                                    (rule: any, index: any) => (
                                                        <ul
                                                            style={{
                                                                paddingLeft: 20,
                                                                margin: 0,
                                                            }}
                                                            key={index}
                                                        >
                                                            <li>{rule}</li>
                                                        </ul>
                                                    ),
                                                )}
                                            </Typography>
                                        </>
                                    )}
                            </Stack>
                        </Box>

                        {value?.type === 'vehicle' && value?.addressVisible && (
                            <Box>
                                <Divider
                                    sx={{
                                        maxWidth: '88%',
                                        color: theme.palette.faded.main,
                                    }}
                                />
                                <Typography
                                    variant='h3'
                                    sx={{
                                        mt: 2,
                                    }}
                                >
                                    Pick Up{' '}
                                    {value.details
                                        .dropoffLocationSameAsPickup &&
                                        'and Drop Off '}{' '}
                                    Location
                                </Typography>
                                <Typography variant='body1'>
                                    {fullAddressPickup}
                                </Typography>
                                {!value.details.dropoffLocationSameAsPickup && (
                                    <>
                                        <Typography
                                            variant='h3'
                                            sx={{
                                                mt: 1.5,
                                            }}
                                        >
                                            Drop Off Location
                                        </Typography>
                                        <Typography variant='body1'>
                                            {fullAddressDropoff}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {!!value?.charges?.length && (
                            <IndividualExperienceCharges
                                charges={value?.charges}
                                experienceType={value?.type}
                                experienceSubType={value?.details?.subType}
                                discounts={value?.discounts}
                                excludedFromBilling={value?.billingExcluded}
                                spaces={value?.spaces}
                                halfDayCheckinTime={value?.halfDayCheckinTime}
                                halfDayCheckoutTime={value?.halfDayCheckoutTime}
                            />
                        )}

                        {!!value?.spaces?.length && (
                            <IndividualExperienceSpaces
                                spaces={value?.spaces}
                            />
                        )}
                    </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Box
                        sx={{
                            position: { md: 'sticky' },
                            top: { md: 24 },
                            alignSelf: { md: 'flex-start' },
                            display: { xs: 'none', md: 'block' },
                        }}
                    >
                        <Stack spacing={3}>
                            {value.availabilityMode ===
                                AvailabilityMode.AddOn &&
                            loggedInUser?.role.includes('guest') ? (
                                <AddOnAlert
                                    experience={value}
                                    allExperiences={allExperiences ?? []}
                                />
                            ) : (
                                <Box id='calendarSection'>
                                    <Typography
                                        component={'h2'}
                                        variant='visuallyHidden'
                                    >
                                        Calendar for reservations
                                    </Typography>
                                    <CalendarComponent
                                        experience={value}
                                        hidePrimaryActionButton={
                                            value.status === 'draft'
                                        }
                                        defaultMonth={
                                            month ? new Date(month) : undefined
                                        }
                                    />
                                </Box>
                            )}
                            <Box>
                                <ContactForm experience={value} />
                            </Box>

                            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                                {shouldShowButton(
                                    loggedInUser,
                                    value,
                                    experience,
                                ) && (
                                    <CustomButton
                                        fullWidth
                                        id='recurringReservation'
                                        variant='outlined'
                                        color='primary'
                                        onClick={onClick}
                                        startIcon={
                                            <AutoAwesomeIcon
                                                sx={{ fontSize: '12px' }}
                                            />
                                        }
                                        sx={{ boxShadow: 3, fontSize: '12px' }}
                                    >
                                        AI Reservation Assistant
                                    </CustomButton>
                                )}
                            </Box>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>

            <Grid
                container
                spacing={{ xs: 3, md: 4 }}
                sx={{ mt: { xs: 3, md: 4 } }}
            >
                {(value?.type === 'property' ||
                    value?.type === 'vehicle' ||
                    value?.type === 'commercial') && (
                    <Grid item xs={12}>
                        <Divider
                            sx={{ mx: 0, color: theme.palette.faded.main }}
                        />
                        <Stack sx={{ pt: 3 }}>
                            <Typography
                                variant='h2'
                                sx={{
                                    mb: 2,
                                }}
                            >
                                {value?.type === 'property'
                                    ? 'What this place offers'
                                    : value?.type === 'vehicle' &&
                                        value?.details.subType === 'car'
                                      ? 'What this car offers'
                                      : value?.type === 'vehicle' &&
                                          value?.details.subType === 'motorbike'
                                        ? 'What this motorbike offers'
                                        : 'Amenities'}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={12}
                                    sx={{ alignItems: 'left' }}
                                >
                                    <AmenitiesComponent
                                        amenities={value?.details}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>
                )}

                <Grid
                    item
                    xs={12}
                    sx={{ display: { xs: 'block', md: 'none' } }}
                >
                    <Stack spacing={3}>
                        <Box id='calendarSection-mobile'>
                            <Typography
                                component={'h2'}
                                variant='visuallyHidden'
                            >
                                Calendar for reservations
                            </Typography>
                            <CalendarComponent
                                experience={value}
                                hidePrimaryActionButton={
                                    value.status === 'draft'
                                }
                                defaultMonth={
                                    month ? new Date(month) : undefined
                                }
                            />
                        </Box>
                        <Box>
                            <ContactForm experience={value} />
                        </Box>
                    </Stack>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ mx: 0, color: theme.palette.faded.main }} />
                    <Stack sx={{ pt: 3 }}>
                        <Typography
                            variant='h2'
                            sx={{
                                mb: 2,
                            }}
                        >
                            Reservation Policy
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6} lg={4}>
                                <ExperiencePolicyCard policy={policy} />
                            </Grid>
                        </Grid>
                    </Stack>
                </Grid>

                {!!reviewsData?.reviews?.length && (
                    <Grid item xs={12}>
                        <Divider
                            sx={{ mx: 0, color: theme.palette.faded.main }}
                        />
                        <Box sx={{ pt: 3 }}>
                            <ThemeProvider theme={ReviewsCustomTheme}>
                                <ExperienceReviewsSection
                                    reviewsData={reviewsData}
                                />
                            </ThemeProvider>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Divider sx={{ mx: 0, color: theme.palette.faded.main }} />
                    <Stack sx={{ pt: 3 }}>
                        <Typography variant='h2' sx={{ mb: 2 }}>
                            {isVehicle ? `Where to pick up` : `Where you'll be`}
                        </Typography>
                        <APIProvider
                            apiKey={
                                process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
                            }
                        >
                            <Box
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    height: { xs: 250, md: 360 },
                                    width: '100%',
                                }}
                            >
                                <GoogleMap
                                    defaultZoom={16}
                                    defaultCenter={position}
                                    style={{ height: '100%', width: '100%' }}
                                    mapId='9064210e52cc2181f0dc0a4e'
                                    disableDefaultUI
                                    fullscreenControl
                                    gestureHandling='greedy'
                                >
                                    {value?.addressVisible ? (
                                        <AdvancedMarker position={position}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 50,
                                                        height: 50,
                                                        borderRadius: '50%',
                                                        backgroundColor:
                                                            'primary.main',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    {EXPERIENCE_TYPE_ICON(
                                                        value?.type,
                                                        {
                                                            color: 'primary.contrastText',
                                                        },
                                                    )}
                                                </Box>
                                                {value?.lastSeenAt?.lat && (
                                                    <Box
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: 0,
                                                            right: 0,
                                                        }}
                                                    >
                                                        <LiveLocationCircle />
                                                    </Box>
                                                )}

                                                <Box
                                                    sx={{
                                                        borderLeft:
                                                            '12px solid transparent',
                                                        borderRight:
                                                            '12px solid transparent',
                                                        borderTop: `10px solid ${theme.palette.primary.main}`,
                                                        mt: '-4px',
                                                    }}
                                                />
                                            </Box>
                                        </AdvancedMarker>
                                    ) : (
                                        <Circle
                                            center={{
                                                lat: value?.lat,
                                                lng: value?.lng,
                                            }}
                                            radius={100}
                                            fillColor={
                                                theme.palette.primary.main
                                            }
                                            strokeColor={
                                                theme.palette.primary.main
                                            }
                                            strokeWeight={1}
                                        />
                                    )}
                                </GoogleMap>
                            </Box>
                        </APIProvider>
                    </Stack>

                    {value?.lastSeenAt?.lat && (
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 1,
                                pt: 0.75,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontStyle: 'italic',
                                }}
                            >
                                This is the live location of this vehicle,
                                updated every 30 minutes.
                            </Typography>
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Divider
                        sx={{ mx: 0, mt: 3, color: theme.palette.faded.main }}
                    />
                    <Box sx={{ pt: 3, pb: 5 }}>
                        <SimilarExperiences
                            current={value}
                            all={allExperiences ? allExperiences : []}
                            radiusKm={15}
                            maxResults={isMobile ? (showAll ? 4 : 2) : 8}
                        />

                        {isMobile &&
                            !showAll &&
                            (allExperiences?.length || 0) > 2 && (
                                <Box textAlign='center' sx={{ mb: 2 }}>
                                    <Button
                                        color='primary'
                                        onClick={() => setShowAll(true)}
                                        sx={{
                                            textTransform: 'none',
                                            fontFamily:
                                                theme.typography.fontFamily,
                                            fontSize: '.75rem',
                                        }}
                                    >
                                        Show more
                                    </Button>
                                </Box>
                            )}
                    </Box>
                </Grid>
            </Grid>
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                message={modalMessage}
                component={modalComponent}
                details={value}
            />
        </Box>
    );
}

const AddOnAlert = ({
    experience,
    allExperiences,
}: {
    experience: Experience;
    allExperiences: Experience[];
}) => {
    const navigate = useNavigate();

    const experienceMap = new Map(allExperiences.map((obj) => [obj.id, obj]));

    return (
        <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                        This experience can’t be reserved on its own
                    </Typography>
                    <Typography color='text.secondary'>
                        {`${experience.name} is offered only as an add-on to ${
                            experience.addOnOf.length > 1
                                ? 'other experiences'
                                : experience.addOnOf[0].name
                        }. 
                        To reserve it, start by creating a reservation for ${
                            experience.addOnOf.length > 1
                                ? 'one of these experiences:'
                                : `${experience.addOnOf[0].name}.`
                        }`}
                    </Typography>
                    <List>
                        {experience.addOnOf.map((addOn) => {
                            const data = experienceMap.get(addOn.id);
                            if (!data) return;

                            return (
                                <ListItemButton
                                    key={addOn.id}
                                    divider
                                    sx={{ gap: 2, p: 1 }}
                                    onClick={() =>
                                        navigate(
                                            `/viewExperiences/${data.type}/${data.id}`,
                                        )
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            src={data.photos[0].src}
                                            variant='rounded'
                                            sx={{ height: 60, width: 60 }}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={data.name}
                                        secondary={`${data.suburb}, ${data.state}`}
                                        primaryTypographyProps={{
                                            sx: { fontWeight: 'bold' },
                                        }}
                                    />
                                    <ListItemSecondaryAction>
                                        <NavigateNext />
                                    </ListItemSecondaryAction>
                                </ListItemButton>
                            );
                        })}
                    </List>
                    <Button
                        size='small'
                        onClick={() => navigate(-1)}
                        startIcon={<ArrowBack />}
                    >
                        Back to all experiences
                    </Button>
                </Stack>
            </Card>
        </Grid>
    );
};
